import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { POLICIES } from './legalLinks';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Shows the tenant's current Privacy Notice or Terms of Use. `html` is the
 * admin-authored copy from PrivacyPolicy/TermsOfService#copy — the same string
 * the public /privacy_policy and /terms_of_service pages render with `raw`.
 */
const PolicyModal = ({ policy, html, loading, error, onClose }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const bodyRef = useRef(null);
  // Callers pass inline arrows; keep the latest without re-running the open/close effect.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const config = POLICIES[policy];

  useEffect(() => {
    if (!config) return undefined;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [config]);

  if (!config) return null;

  // The copy carries its own table of contents (href="#section"). Scroll within
  // the modal instead of letting the hash change scroll the page behind it.
  const onBodyClick = (e) => {
    const anchor = e.target.closest && e.target.closest('a[href^="#"]');
    if (!anchor || !bodyRef.current) return;
    const id = anchor.getAttribute('href').slice(1);
    if (!id) return;
    const target = bodyRef.current.querySelector(`[id="${id.replace(/"/g, '\\"')}"]`);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const titleId = `succession-policy-modal-title-${policy}`;
  const hasCopy = typeof html === 'string' && html.trim() !== '';

  let body;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-16" role="status">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-succession-border border-t-succession-primary" />
        <span className="sr-only">
          <FormattedMessage id="succession.legal.loading" defaultMessage="Loading…" />
        </span>
      </div>
    );
  } else if (error || !hasCopy) {
    body = (
      <div className="px-6 py-12 text-center text-sm text-succession-text-secondary">
        <p className="mb-3">
          <FormattedMessage id="succession.legal.unavailable" defaultMessage="This policy is not available." />
        </p>
        <a href={config.href} target="_blank" rel="noopener noreferrer" className="text-succession-primary underline">
          <FormattedMessage id="succession.legal.open_in_new_tab" defaultMessage="Open in new tab" />
        </a>
      </div>
    );
  } else {
    body = (
      // eslint-disable-next-line react/no-danger
      <div ref={bodyRef} onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: html }} />
    );
  }

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 2147483000, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="policy-modal-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex w-full flex-col overflow-hidden rounded-lg border border-succession-border bg-succession-bg-primary shadow-succession-lg"
        style={{ maxWidth: '880px', maxHeight: '85vh' }}
      >
        <div className="flex items-center justify-between gap-4 border-b border-succession-border px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-succession-text-primary">
            <FormattedMessage id={config.titleId} defaultMessage={config.titleDefault} />
          </h2>
          <div className="flex items-center gap-3">
            <a
              href={config.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-succession-text-secondary hover:text-succession-primary"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
              <FormattedMessage id="succession.legal.open_in_new_tab" defaultMessage="Open in new tab" />
            </a>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1 text-succession-text-secondary hover:bg-succession-bg-secondary hover:text-succession-text-primary"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        {/* The admin copy ships its own light palette (dark text), so its panel
            stays white in dark mode rather than taking the Succession surface. */}
        <div className="flex-1 overflow-y-auto bg-white text-gray-900">
          {body}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PolicyModal;
